import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { setCurrentUser, fetchCurrentUser } from '../../ducks/user.duck';
import { queryOnboardingListing } from '../../ducks/onboarding.duck';

// Profile updates must ask for the file attachments back, same reasoning as
// ManageAccountPage.duck.js: mergeCurrentUser shallow-merges relationships and
// keeps whatever the response omits.
const CURRENT_USER_QUERY_PARAMS = {
  expand: true,
  include: ['profileImage', 'privateFileAttachments', 'privateFileAttachments.file'],
};

// ================ Async Thunks ================ //

////////////////////////////////
// Submit registration (food company only — see docs/onboarding-spec.md §6.2)
////////////////////////////////

// The `registration` key is replaced whole on every write (update_profile does
// a shallow merge on TOP-LEVEL extended-data keys only), so any existing
// registration object has to be read back and spread first — the same rule
// ManageAccountPage.duck.js follows for `documentSlots`. `protectedData.terms`
// is a sibling top-level key and is preserved automatically by the merge.
const submitRegistrationPayloadCreator = (
  { declarations },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  const currentUser = getState().user.currentUser;
  const existingRegistration = currentUser?.attributes?.profile?.protectedData?.registration || {};

  return sdk.currentUser
    .updateProfile(
      {
        protectedData: {
          registration: {
            ...existingRegistration,
            submittedAt: new Date().toISOString(),
            declarations,
          },
        },
      },
      CURRENT_USER_QUERY_PARAMS
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));
      return entities[0];
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const submitRegistrationThunk = createAsyncThunk(
  'OnboardingPage/submitRegistration',
  submitRegistrationPayloadCreator
);

export const submitRegistration = declarations => dispatch =>
  dispatch(submitRegistrationThunk({ declarations })).unwrap();

// ================ Slice ================ //

const onboardingPageSlice = createSlice({
  name: 'OnboardingPage',
  initialState: {
    submitInProgress: false,
    submitError: null,
  },
  reducers: {
    clearSubmitError: state => {
      state.submitError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(submitRegistrationThunk.pending, state => {
        state.submitInProgress = true;
        state.submitError = null;
      })
      .addCase(submitRegistrationThunk.fulfilled, state => {
        state.submitInProgress = false;
      })
      .addCase(submitRegistrationThunk.rejected, (state, action) => {
        state.submitInProgress = false;
        state.submitError = action.payload;
      });
  },
});

export const { clearSubmitError } = onboardingPageSlice.actions;

export default onboardingPageSlice.reducer;

// ================ Load data ================ //

// Mirrors NewListingPage.duck.js's loadData: currentUser plus the profile
// listing query. Harmless no-op for a food-processor (postListings: false, so
// the query returns no listings) — getOnboardingStage never reads it for them.
export const loadData = () => dispatch =>
  Promise.all([dispatch(fetchCurrentUser()), dispatch(queryOnboardingListing())]);
